/**
 * Invoice API
 * Specific invoice-related API calls
 */

import { apiClient } from './client';
import type { GenerateInvoiceResponse, InvoiceHistoryResponse, Invoice, RateLimitResponse } from './types';

export const invoiceApi = {
  /**
   * Generate invoice from text
   */
  async generateInvoice(text: string, sessionId?: string): Promise<GenerateInvoiceResponse> {
    return apiClient.post<GenerateInvoiceResponse>('/invoice/generate', {
      text,
      sessionId,
    });
  },

  /**
   * Get invoice by ID
   */
  async getInvoice(id: string): Promise<{ success: boolean; invoice?: Invoice; error?: string }> {
    return apiClient.get(`/invoice/${id}`);
  },

  /**
   * Update invoice
   */
  async updateInvoice(
    id: string,
    updates: Partial<Invoice>
  ): Promise<{ success: boolean; invoice?: Invoice; error?: string }> {
    return apiClient.put(`/invoice/${id}`, updates);
  },

  /**
   * Delete invoice
   */
  async deleteInvoice(
    id: string
  ): Promise<{ success: boolean; message?: string; error?: string }> {
    return apiClient.delete(`/invoice/${id}`);
  },

  /**
   * Get invoice history
   */
  async getHistory(
    sessionId: string,
    limit: number = 10,
    offset: number = 0
  ): Promise<InvoiceHistoryResponse> {
    return apiClient.get(`/history?sessionId=${sessionId}&limit=${limit}&offset=${offset}`);
  },

  /**
   * Get recent invoices (cached)
   */
  async getRecent(sessionId: string): Promise<{
    success: boolean;
    recent?: Array<{ id: string; number: string; timestamp: number }>;
    error?: string;
  }> {
    return apiClient.get(`/history/recent?sessionId=${sessionId}`);
  },

  /**
   * Get current rate limit status
   */
  async getRateLimitStatus(sessionId: string): Promise<RateLimitResponse> {
    return apiClient.get(`/invoice/limit/status?sessionId=${sessionId}`);
  },

  /**
   * Add bonus invoices (after watching ads)
   */
  async addBonusInvoices(sessionId: string, bonusCount: number = 5): Promise<RateLimitResponse> {
    return apiClient.post('/invoice/limit/bonus', { sessionId, bonusCount });
  },
};
